/**
 * Cross-Domain Device Fingerprint Utility
 *
 * This module generates a consistent device fingerprint that remains the same
 * across different domains by focusing exclusively on hardware and OS characteristics
 * that don't change between domains.
 */

// Type definitions for APIs that TypeScript might not recognize
interface NavigatorWithBattery extends Navigator {
  getBattery?: () => Promise<{
    level: number;
    charging: boolean;
  }>;
}

interface WebGLRenderingContext {
  VENDOR: number;
  RENDERER: number;
  getParameter(pname: number): any;
}

// Only include properties that are guaranteed to be consistent across domains
interface StableDeviceData {
  // Screen properties - hardware specific
  screenWidth: number;
  screenHeight: number;
  colorDepth: number;
  pixelRatio: number;
  screenOrientation: string;

  // Media capabilities that reflect hardware
  audioChannels: number;
  audioSampleRate: number;
  videoBitrate: number;
  videoFrameRate: number;

  // OS and hardware capabilities
  platform: string;
  hardwareConcurrency: number;
  deviceMemory?: number;
  maxTouchPoints: number;
  batteryLevel?: number;
  batteryCharging?: boolean;

  // Timezone - location specific
  timezoneOffset: number;
  timezone: string;

  // Connection capabilities
  connectionDownlink?: number;
  connectionEffectiveType?: string;

  // GPU info - hardware specific
  gpuVendor?: string;
  gpuRenderer?: string;
}

/**
 * Generates a hash from a string using a consistent algorithm
 * @param str String to hash
 * @returns A hash representation of the string
 */
const hashString = (str: string): string => {
  let hash = 0;
  if (str.length === 0) return hash.toString(16);

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return hash.toString(16);
};

/**
 * Gets basic WebGL information that is hardware-dependent
 * @returns An object with GPU vendor and renderer info
 */
const getGpuInfo = () => {
  if (typeof document === 'undefined') {
    return { gpuVendor: undefined, gpuRenderer: undefined };
  }

  try {
    const canvas = document.createElement('canvas');
    // Cast to WebGLRenderingContext to satisfy TypeScript
    const gl = canvas.getContext('webgl') as WebGLRenderingContext ||
      canvas.getContext('experimental-webgl') as WebGLRenderingContext;

    if (!gl) {
      return { gpuVendor: undefined, gpuRenderer: undefined };
    }

    // Get GPU info but only return simple vendor identifiers, not the full detailed information
    // that might vary across domains
    const vendor = gl.getParameter(gl.VENDOR);
    const renderer = gl.getParameter(gl.RENDERER);

    // We only care about the company/device identifiers, not specific versions
    // So we'll extract just the company name and main model to be more stable
    const simplifiedVendor = typeof vendor === 'string' ?
      vendor.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10) : undefined;
    const simplifiedRenderer = typeof renderer === 'string' ?
      renderer.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10) : undefined;

    return {
      gpuVendor: simplifiedVendor,
      gpuRenderer: simplifiedRenderer
    };
  } catch (e) {
    return { gpuVendor: undefined, gpuRenderer: undefined };
  }
};

/**
 * Get information about media capabilities that are hardware-dependent
 * @returns Object containing audio and video capabilities
 */
const getMediaCapabilities = async () => {
  if (typeof navigator === 'undefined' || !navigator.mediaCapabilities) {
    return {
      audioChannels: 2, // default stereo
      audioSampleRate: 44100, // default CD quality
      videoBitrate: 0,
      videoFrameRate: 0
    };
  }

  try {
    // Try to query for video capabilities
    const videoConfig = {
      type: 'file',
      video: {
        contentType: 'video/mp4; codecs="avc1.42E01E"',
        width: 1920,
        height: 1080,
        bitrate: 5000000,
        framerate: 60
      }
    };

    const videoDecoding = await navigator.mediaCapabilities.decodingInfo(videoConfig as any);

    // Try to query for audio capabilities
    const audioConfig = {
      type: 'file',
      audio: {
        contentType: 'audio/mp4; codecs="mp4a.40.2"',
        channels: 6,
        bitrate: 320000,
        samplerate: 48000
      }
    };

    const audioDecoding = await navigator.mediaCapabilities.decodingInfo(audioConfig as any);

    return {
      audioChannels: audioDecoding?.supported ? 6 : 2,
      audioSampleRate: audioDecoding?.supported ? 48000 : 44100,
      videoBitrate: videoDecoding?.supported ? 5000000 : 0,
      videoFrameRate: videoDecoding?.supported ? 60 : 30
    };
  } catch (e) {
    // Default values if we can't detect
    return {
      audioChannels: 2,
      audioSampleRate: 44100,
      videoBitrate: 0,
      videoFrameRate: 30
    };
  }
};

/**
 * Get battery information if available
 * @returns Battery status or undefined if not available
 */
const getBatteryInfo = async () => {
  if (typeof navigator === 'undefined') {
    return { batteryLevel: undefined, batteryCharging: undefined };
  }

  try {
    // Cast navigator to include the battery API
    const nav = navigator as NavigatorWithBattery;

    if (!nav.getBattery) {
      return { batteryLevel: undefined, batteryCharging: undefined };
    }

    const battery = await nav.getBattery();
    return {
      batteryLevel: Math.round(battery.level * 100) / 100, // Round to 2 decimal places
      batteryCharging: battery.charging
    };
  } catch (e) {
    return { batteryLevel: undefined, batteryCharging: undefined };
  }
};

/**
 * Get network connection information
 * @returns Connection type and speed or undefined if not available
 */
const getConnectionInfo = () => {
  if (typeof navigator === 'undefined' || !('connection' in navigator)) {
    return { connectionDownlink: undefined, connectionEffectiveType: undefined };
  }

  try {
    const connection = (navigator as any).connection;
    return {
      connectionDownlink: connection.downlink,
      connectionEffectiveType: connection.effectiveType
    };
  } catch (e) {
    return { connectionDownlink: undefined, connectionEffectiveType: undefined };
  }
};

/**
 * Collects only the most stable device characteristics that don't vary across domains
 * @returns An object containing collected stable device data
 */
const collectStableData = async (): Promise<StableDeviceData> => {
  // Handle server-side rendering
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      screenWidth: 0,
      screenHeight: 0,
      colorDepth: 0,
      pixelRatio: 1,
      screenOrientation: '',
      audioChannels: 0,
      audioSampleRate: 0,
      videoBitrate: 0,
      videoFrameRate: 0,
      platform: '',
      hardwareConcurrency: 0,
      maxTouchPoints: 0,
      timezoneOffset: 0,
      timezone: ''
    };
  }

  // Get screen orientation in a consistent format
  let screenOrientation = '';
  if (window.screen && window.screen.orientation) {
    screenOrientation = window.screen.orientation.type || '';
  }

  // Collect additional hardware data
  const gpuInfo = getGpuInfo();
  const mediaCapabilities = await getMediaCapabilities();
  const batteryInfo = await getBatteryInfo();
  const connectionInfo = getConnectionInfo();

  return {
    // Screen properties are hardware-specific and don't change across domains
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio || 1,
    screenOrientation: screenOrientation,

    // Media capabilities
    ...mediaCapabilities,

    // OS and hardware capabilities
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: (navigator as any).deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    ...batteryInfo,

    // Timezone info is based on system settings
    timezoneOffset: new Date().getTimezoneOffset(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Network capabilities
    ...connectionInfo,

    // GPU information
    ...gpuInfo
  };
};

// A consistent key for localStorage that should be used across all domains
const FINGERPRINT_KEY = 'inspired_cross_domain_fingerprint';

/**
 * Generates a device fingerprint based only on stable cross-domain properties
 * @returns A string that uniquely identifies the device/browser across domains
 */
export const generateDeviceFingerprint = async (): Promise<string> => {
  try {
    const stableData = await collectStableData();
    const dataString = JSON.stringify(stableData);
    return hashString(dataString);
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    // Fallback to a very basic fingerprint using screen dimensions and platform
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      return hashString(`${window.screen.width}x${window.screen.height}|${navigator.platform}`);
    }
    return 'unknown-device';
  }
};

/**
 * Gets a cached device fingerprint or generates a new one
 * @returns The device fingerprint string
 */
export const getDeviceFingerprint = async (): Promise<string> => {
  // Try to get from localStorage first for performance
  if (typeof window !== 'undefined' && window.localStorage) {
    const cached = localStorage.getItem(FINGERPRINT_KEY);
    if (cached && cached.length > 8) {
      return cached;
    }
  }

  // Generate a new fingerprint
  const fingerprint = await generateDeviceFingerprint();

  // Store in localStorage if available (helps with persistence)
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      localStorage.setItem(FINGERPRINT_KEY, fingerprint);
    } catch (e) {
      console.warn('Could not store device fingerprint in localStorage', e);
    }
  }

  return fingerprint;
};

export default getDeviceFingerprint;
